import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { formatCurrency } from "../lib/formatCurrency";
import { formatNumber } from "../lib/formatNumber";
import CountUp from "react-countup";
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Plus, Search, Building2, User, Mail, Phone, MapPin, 
  Globe, MoreVertical, TrendingUp, Wallet, FileText, Tag, Download, Eye, Lock
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { CRMRecordPanel } from '../components/CRMRecordPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INDUSTRIES = [
  'Technology', 'Finance', 'Healthcare', 'Education', 'Manufacturing',
  'Retail', 'Construction', 'Agriculture', 'Mining', 'Tourism',
  'Professional Services', 'Government', 'Non-Profit', 'Other'
];

const SOURCES = ['referral', 'website', 'cold_call', 'event', 'social_media', 'advertising', 'other'];

const statusColors = {
  active: 'bg-emerald-100 text-emerald-700',
  inactive: 'bg-slate-100 text-slate-700',
  prospect: 'bg-blue-100 text-blue-700'
};



const validatePhone = (phone) => {
  if (!phone) return true;

  const trimmed = phone.trim();

  const regex = /^(\+27|0)[0-9]{9}$/;

  return regex.test(trimmed);
};

const validateVAT = (vat) => {
  if (!vat) return true; // allow empty if optional

  const trimmed = vat.trim();

  const regex = /^4\d{9}$/;

  return regex.test(trimmed);
};

const validateCompanyName = (name) => {
  const regex = /^[A-Za-z0-9&.\-()'\s]{2,100}$/;
  return regex.test(name);
};

const validateContactName = (name) => {
  if (!name) return false;

  const trimmed = name.trim();
  const regex = /^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/;

  return regex.test(trimmed);
};

const validateWebsite = (website) => {
  if (!website) return true; // allow empty if optional

  const trimmed = website.trim();

  const regex = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/i;

  return regex.test(trimmed);
};

const validateEmail = (email) => {
  if (!email) return true; // allow empty if email not required

  const trimmed = email.trim();

  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return regex.test(trimmed);
};

const validateRegistration = (reg) => {
  if (!reg) return true; // allow empty if optional

  const trimmed = reg.trim();

  const regex = /^\d{4}\/\d{6}\/\d{2}$/;

  return regex.test(trimmed);
};

// Client Form Component
const ClientForm = ({ client, onSave, onClose }) => {
  const [form, setForm] = useState({
    company: client?.company || '',
    contact_name: client?.contact_name || '',
    contact_email: client?.contact_email || '',
    contact_phone: client?.contact_phone || '',
    industry: client?.industry || '',
    location: client?.location || '',
    vat_number: client?.vat_number || '',
    registration_number: client?.registration_number || '',
    website: client?.website || '',
    billing_address: client?.billing_address || '',
    status: client?.status || 'active',
    account_manager: client?.account_manager || '',
    credit_limit: client?.credit_limit || 0,
    payment_terms: client?.payment_terms || 30,
    source: client?.source || '',
    tags: client?.tags || [],
    notes: client?.notes || '',
    total_revenue: client?.total_revenue || 0,
    outstanding_balance: client?.outstanding_balance || 0
  });
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);

  const addTag = () => {
    if (tagInput && !form.tags.includes(tagInput)) {
      setForm({ ...form, tags: [...form.tags, tagInput] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setForm({ ...form, tags: form.tags.filter(t => t !== tag) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

  const cleanedCompany = form.company.replace(/[<>]/g, "").trim();
  const cleanedContact = form.contact_name.replace(/[<>]/g, "").trim();

    let cleanedWebsite = form.website.trim();

  if (cleanedWebsite && !cleanedWebsite.startsWith("http")) {
    cleanedWebsite = "https://" + cleanedWebsite;
  }
    
if (!validateCompanyName(form.company.trim())) {
  toast.error("Invalid company name");
  setSaving(false);
  return false;
}

if (!validateWebsite(form.website)) {
  toast.error("Invalid website URL");
  setSaving(false);
  return;
}

if (!validatePhone(form.contact_phone)) {
  toast.error("Invalid phone number");
  setSaving(false);
  return;
}

if (!validateEmail(form.contact_email)) {
  toast.error("Invalid email address");
  setSaving(false);
  return;
}

if (!validateVAT(form.vat_number)) {
  toast.error("Invalid VAT number");
  setSaving(false);
  return;
}

if (!validateRegistration(form.registration_number)) {
  toast.error("Invalid company registration number");
  setSaving(false);
  return;
}

console.log("Contact value:", form.contact_name);
console.log("Validation result:", validateContactName(form.contact_name));

if (!validateContactName(form.contact_name.trim())) {
  toast.error("Invalid contact name");
  setSaving(false);
  return false;
}
    try {
    if (client?.id) {
  await axios.put(`${API}/clients/${client.id}`, {
    ...form,
    company: cleanedCompany,
    contact_name: cleanedContact,
    website: cleanedWebsite
  });

  toast.success('Client updated');

} else {
  await axios.post(`${API}/clients`, {
    ...form,
    company: cleanedCompany,
    contact_name: cleanedContact,
    website: cleanedWebsite
  });

  toast.success('Client created');
}
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      {/* Company Info */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2 col-span-2">
  <Label>Company Name *</Label>
 <Input
  value={form.company}
  onChange={(e) => setForm({ ...form, company: e.target.value })}
  required
  maxLength={100}
  placeholder="ABC Corporation (Pty) Ltd"
  data-testid="client-company-input"
/>
</div>
        <div className="space-y-2">
          <Label>Industry</Label>
          <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
            <SelectTrigger><SelectValue placeholder="Select industry" /></SelectTrigger>
            <SelectContent>
              {INDUSTRIES.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Location</Label>
          <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Johannesburg, Gauteng" />
        </div>
      </div>

      {/* Contact Info */}
      <div className="pt-4 border-t">
        <h3 className="font-semibold text-sm mb-3">Primary Contact</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Contact Name *</Label>
            <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} required placeholder="John Smith" />
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input
  type="email"
  value={form.contact_email}
  onChange={(e) =>
    setForm({
      ...form,
      contact_email: e.target.value.trim()
    })
  }
  placeholder="john@company.co.za"
/>
          </div>
          <div className="space-y-2">
            <Label>Phone</Label>
            <Input
  value={form.contact_phone}
  onChange={(e) => {
    const value = e.target.value;

    if (/^[0-9+\s()-]*$/.test(value)) {
      setForm({
        ...form,
        contact_phone: value
      });
    }
  }}
  maxLength={20}
  placeholder="+27 11 123 4567"
/>
          </div>
          <div className="space-y-2">
            <Label>Website</Label>
            <Input
  value={form.website}
  onChange={(e) =>
    setForm({
      ...form,
      website: e.target.value.trim()
    })
  }
  placeholder="www.company.co.za"
/>
          </div>
        </div>
      </div>

      {/* Business Details */}
      <div className="pt-4 border-t">
        <h3 className="font-semibold text-sm mb-3">Business Details</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>VAT Number</Label>
            <Input
  value={form.vat_number}
  onChange={(e) => {
    const value = e.target.value;

    if (/^\d*$/.test(value) && value.length <= 10) {
      setForm({ ...form, vat_number: value });
    }
  }}
  placeholder="4123456789"
/>
          </div>
          <div className="space-y-2">
            <Label>Registration Number</Label>
            <Input
  value={form.registration_number}
  onChange={(e) => {
    const value = e.target.value;

    if (/^[0-9/]*$/.test(value) && value.length <= 14) {
      setForm({
        ...form,
        registration_number: value
      });
    }
  }}
  placeholder="2023/123456/07"
/>
          </div>
          <div className="space-y-2 col-span-2">
            <Label>Billing Address</Label>
            <Textarea value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} rows={2} placeholder="123 Main Street, Sandton, 2196" />
          </div>
        </div>
      </div>

{/* Account Settings */}
<div className="pt-4 border-t">
  <h3 className="font-semibold text-sm mb-3">Account Settings</h3>
  <div className="grid grid-cols-3 gap-4">
    {/* Status */}
    <div className="space-y-2">
      <Label>Status</Label>
      <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="active">Active</SelectItem>
          <SelectItem value="inactive">Inactive</SelectItem>
          <SelectItem value="prospect">Prospect</SelectItem>
        </SelectContent>
      </Select>
    </div>

    {/* Credit Limit */}
    <div className="space-y-2">
      <Label>Credit Limit (ZAR)</Label>
      <Input
        type="number"
        min="0"
        value={form.credit_limit}
        onChange={(e) => setForm({ ...form, credit_limit: parseFloat(e.target.value) || 0 })}
      />
    </div>

    {/* Payment Terms */}
    <div className="space-y-2">
      <Label>Payment Terms (Days)</Label>
      <Select value={String(form.payment_terms)} onValueChange={(v) => setForm({ ...form, payment_terms: parseInt(v) })}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="7">7 Days</SelectItem>
          <SelectItem value="14">14 Days</SelectItem>
          <SelectItem value="30">30 Days</SelectItem>
          <SelectItem value="45">45 Days</SelectItem>
          <SelectItem value="60">60 Days</SelectItem>
          <SelectItem value="90">90 Days</SelectItem>
        </SelectContent>
      </Select>
    </div>
  </div>
  
  {/* Revenue & Outstanding Row */}
  <div className="grid grid-cols-2 gap-4 mt-4">
    <div className="space-y-2">
      <Label>Revenue</Label>
      <Input
        type="number"
        min="0"
        value={form.total_revenue}
        onChange={(e) => setForm({ ...form, total_revenue: parseFloat(e.target.value) || 0 })}
        placeholder="0"
      />
    </div>
    <div className="space-y-2">
      <Label>Outstanding</Label>
      <Input
        type="number"
        min="0"
        value={form.outstanding_balance}
        onChange={(e) => setForm({ ...form, outstanding_balance: parseFloat(e.target.value) || 0 })}
        placeholder="0"
      />
    </div>
  </div>
</div>

      {/* Source & Tags */}
      <div className="pt-4 border-t">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Source</Label>
            <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
              <SelectTrigger><SelectValue placeholder="How did they find you?" /></SelectTrigger>
              <SelectContent>
                {SOURCES.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Tags</Label>
            <div className="flex gap-2">
              <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="Add tag" onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())} />
              <Button type="button" variant="outline" size="sm" onClick={addTag}>Add</Button>
            </div>
            {form.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {form.tags.map(tag => (
                  <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                    {tag} ×
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="Internal notes about this client..." />
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t sticky bottom-0 bg-background">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="save-client-btn">{saving ? 'Saving...' : 'Save Client'}</Button>
      </div>
    </form>
  );
};

// Client Card Component
const ClientCard = ({ client, onClick, onDelete, onDownloadStatement, onViewDetails }) => (
  <Card className={`cursor-pointer hover:shadow-md transition-shadow ${client._is_locked ? 'border-l-4 border-l-red-400' : ''}`} data-testid={`client-card-${client.id}`}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3" onClick={onClick}>
          <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
            {client._is_locked ? <Lock className="h-5 w-5 text-red-500" /> : <Building2 className="h-5 w-5 text-slate-600" />}
          </div>
          <div>
            <p className="font-semibold">{client.company}</p>
            <p className="text-sm text-muted-foreground">{client.client_number}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={statusColors[client.status]}>{client.status}</Badge>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" onClick={(e) => e.stopPropagation()}>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onViewDetails(); }}>
                <Eye className="h-4 w-4 mr-2" /> View Details
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onClick(); }}>
                <FileText className="h-4 w-4 mr-2" /> Edit
              </DropdownMenuItem>
               <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDownloadStatement(); }}>
                <Download className="h-4 w-4 mr-2" /> Download Statement
              </DropdownMenuItem> 
              <DropdownMenuItem className="text-rose-600" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2 text-muted-foreground">
          <User className="h-4 w-4" />
          <span>{client.contact_name}</span>
        </div>
        {client.contact_email && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Mail className="h-4 w-4" />
            <span className="truncate">{client.contact_email}</span>
          </div>
        )}
        {client.location && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <MapPin className="h-4 w-4" />
            <span>{client.location}</span>
          </div>
        )}
        {client.industry && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Tag className="h-4 w-4" />
            <span>{client.industry}</span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-4 pt-4 border-t">
        <div>
          <p className="text-xs text-muted-foreground">Revenue</p>
          <p className="font-mono font-semibold text-emerald-600">{formatCurrency(client.total_revenue || 0)}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">Outstanding</p>
          <p className={`font-mono font-semibold ${(client.outstanding_balance || 0) > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
            {formatCurrency(client.outstanding_balance || 0)}
          </p>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Main Clients Page
export const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterIndustry, setFilterIndustry] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [viewingClientId, setViewingClientId] = useState(null);
  const [viewMode, setViewMode] = useState('cards'); // cards or table

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`);
      setClients(res.data);
    } catch (error) {
      console.error('Failed to load clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    try {
      await axios.delete(`${API}/clients/${id}`);
      toast.success('Client deleted');
      loadClients();
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const handleDownloadStatement = async (client) => {
    try {
      const response = await axios.get(`${API}/clients/${client.id}/statement/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `statement_${client.company.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Statement PDF downloaded');
    } catch (error) {
      toast.error('Failed to download statement');
    }
  };

  const filteredClients = clients.filter(c => {
    const matchesSearch = c.company.toLowerCase().includes(search.toLowerCase()) ||
      c.contact_name.toLowerCase().includes(search.toLowerCase()) ||
      c.client_number.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = filterStatus === 'all' || c.status === filterStatus;
    const matchesIndustry = filterIndustry === 'all' || c.industry === filterIndustry;
    return matchesSearch && matchesStatus && matchesIndustry;
  });

  const uniqueIndustries = [...new Set(clients.map(c => c.industry).filter(Boolean))];

  // Summary stats
  const totalClients = clients.length;
  const activeClients = clients.filter(c => c.status === 'active').length;
  // Define what statuses are considered inactive
// Define what statuses are considered inactive
const inactiveStatuses = ['inactive']; // adjust to your app
  // Count inactive clients
const inactiveClients = clients.filter(c => inactiveStatuses.includes(c.status)).length;
// const prospectClient = ['prospect']; // adjust to your app
//   // Count inactive clients
// const prospectClient = clients.filter(c => prospectClient.includes(c.status)).length;
  const totalRevenue = clients.reduce((sum, c) => sum + (c.total_revenue || 0), 0);
  const totalOutstanding = clients.reduce((sum, c) => sum + (c.outstanding_balance || 0), 0);

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-pulse">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Clients</h1>
          <p className="text-muted-foreground mt-1">Build and manage stronger client relationships</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
            onClick={() => setSelectedClient(null)} 
            data-testid="add-client-btn"
            className="bg-green-600 hover:bg-green-700 text-white transition-colors duration-200"
            >
            <Plus className="h-4 w-4 mr-2" /> 
            Add Client
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{selectedClient ? 'Edit Client' : 'New Client'}</DialogTitle>
            </DialogHeader>
            <ClientForm client={selectedClient} onSave={loadClients} onClose={() => setDialogOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="kpi-card relative overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <Building2 className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Clients</p>
                <p className="text-2xl font-bold font-mono">
  <CountUp end={totalClients} duration={1} />
</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card relative overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Clients</p>
                <p className="text-2xl font-bold font-mono text-emerald-600">
  <CountUp end={activeClients} duration={1} />
</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card relative overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Inactive Clients</p>
                <p className="text-2xl font-bold font-mono text-emerald-600">
  <CountUp end={inactiveClients} duration={1} />
</p>
              </div>
            </div>
          </CardContent>
        </Card>


        <Card className="kpi-card relative overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center">
                <Wallet className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Revenue</p>
                <p className="text-2xl font-bold font-mono"><CountUp
  end={totalRevenue}
  duration={1.5}
  formattingFn={formatCurrency}
/></p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card relative overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-rose-100 flex items-center justify-center">
                <FileText className="h-6 w-6 text-rose-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Outstanding</p>
                <p className="text-2xl font-bold font-mono text-rose-600"><CountUp
  end={totalOutstanding}
  duration={1.5}
  formattingFn={formatCurrency}
/></p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search clients..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="client-search" />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="prospect">Prospect</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterIndustry} onValueChange={setFilterIndustry}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Industry" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Industries</SelectItem>
            {uniqueIndustries.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex border rounded-md">
          <Button variant={viewMode === 'cards' ? 'secondary' : 'ghost'} size="sm" onClick={() => setViewMode('cards')}>Cards</Button>
          <Button variant={viewMode === 'table' ? 'secondary' : 'ghost'} size="sm" onClick={() => setViewMode('table')}>Table</Button>
        </div>
      </div>

      {/* Client List */}
      {viewMode === 'cards' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClients.map(client => (
            <ClientCard
              key={client.id}
              client={client}
              onClick={() => { setSelectedClient(client); setDialogOpen(true); }}
              onDelete={() => handleDelete(client.id)}
              onDownloadStatement={() => handleDownloadStatement(client)}
              onViewDetails={() => setViewingClientId(client.id)}
            />
          ))}
          {filteredClients.length === 0 && (
            <div className="col-span-full text-center py-12 text-muted-foreground">
              No clients found
            </div>
          )}
        </div>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client #</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Industry</TableHead>
                <TableHead>Location</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
                <TableHead className="text-right">Outstanding</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredClients.map(client => (
                <TableRow key={client.id}>
                  <TableCell className="font-mono text-sm">{client.client_number}</TableCell>
                  <TableCell className="font-medium">{client.company}</TableCell>
                  <TableCell>
                    <div>
                      <p className="text-sm">{client.contact_name}</p>
                      {client.contact_email && <p className="text-xs text-muted-foreground">{client.contact_email}</p>}
                    </div>
                  </TableCell>
                  <TableCell>{client.industry || '-'}</TableCell>
                  <TableCell>{client.location || '-'}</TableCell>
                  <TableCell className="text-right font-mono">{formatCurrency(client.total_revenue)}</TableCell>
                  <TableCell className="text-right font-mono text-amber-600">{formatCurrency(client.outstanding_balance)}</TableCell>
                  <TableCell><Badge className={statusColors[client.status]}>{client.status}</Badge></TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm"><MoreVertical className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => { setSelectedClient(client); setDialogOpen(true); }}>Edit</DropdownMenuItem>
                        <DropdownMenuItem className="text-rose-600" onClick={() => handleDelete(client.id)}>Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {filteredClients.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No clients found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* CRM Record Intelligence Panel */}
      {viewingClientId && (
        <div className="mt-4" data-testid="client-detail-panel">
          <CRMRecordPanel recordType="client" recordId={viewingClientId} onClose={() => setViewingClientId(null)} />
        </div>
      )}
    </div>
  );
};
